import { useCallback, useState } from 'react';

type SessionPersistentStorage<T> = {
  value?: T;
};

type SetStateAction<S> = S | ((prevState: S) => S);

/**
 * Generic useState() replacement that persists values to sessionStorage.
 */
export function useSessionPersistentState<T>({
  key,
  defaultValue,
}: {
  key: string;
  defaultValue: T;
}): [T, (newValue: SetStateAction<T>) => void] {
  const [currentValue, setCurrentValue] = useState<T>(() => {
    // Get values from SessionStorage, or fail to defaults
    let storedValue: SessionPersistentStorage<T>;
    try {
      storedValue = JSON.parse(
        sessionStorage.getItem(key) || '{}',
      ) as SessionPersistentStorage<T>;
    } catch {
      sessionStorage.deleteItem(key);
      storedValue = {} as SessionPersistentStorage<T>;
    }

    return storedValue.value || defaultValue;
  });

  const updateValue = useCallback(
    (newValue: SetStateAction<T>) => {
      setCurrentValue(prevValue => {
        const actualNewValue =
          typeof newValue === 'function'
            ? (newValue as (prevState: T) => T)(prevValue)
            : newValue;

        sessionStorage.setItem(
          key,
          JSON.stringify({
            value: actualNewValue,
          } as SessionPersistentStorage<T>),
        );
        return actualNewValue;
      });
    },
    [setCurrentValue, key],
  );
  return [currentValue, updateValue];
}
